/**
 * @module Components.SignUp
 *
 */
import React, { useState, FormEvent } from "react";
import { MessageDescriptor } from "react-intl";
import { useError } from "app/utils/hooks";
import { AuthService } from "app/services";
import { UserModel } from "app/models";
import { cls } from "app/utils";
import { RoleSelect, SportSelect } from "app/components/inputs/SelectBox";
import { DefaultOption } from "app/components/inputs/SelectBox/Base";
import ImageCheckbox, {
  EventState,
} from "app/components/inputs/CheckBox/Image";
import role_athlete_png from "app/images/role-athlete.png";
import role_coach_png from "app/images/role-coach.png";
import role_fan_png from "app/images/role-fan.png";
import styles from "./styles.module.scss";
import { validateProperty } from "./validate";
import FormItem from "app/components/inputs/FormItem";
import { useTranslation } from "react-i18next";

type State = {
  user: Partial<UserModel> | null;
  kind: KindType | null;
  sport: string | null;
  title: string | null;
};

type PropsDef = {
  user: Partial<UserModel>;
  coachSignup: boolean;
  onComplete: (data: Partial<State>) => void;
  // Sport-only mode (trial links): the role stays coach and only the sport
  // question is asked, prefilled from the link or Golf.
  sportOnly?: boolean;
};

type FormErrors = {
  kind?: string | MessageDescriptor;
};

const initErrors: FormErrors = {};

type KindType = "coach" | "athlete" | "other";

const initState: State = {
  user: null,
  kind: null,
  sport: null,
  title: null,
};

export default function AboutYou(props: PropsDef) {
  const { t } = useTranslation();
  const [state, setState] = useState(() => ({
    ...initState,
    user: props.user || null,
    kind: props.coachSignup || props.sportOnly ? ("coach" as KindType) : null,
    // A sport named in the checkout link (analyze funnels) pre-fills the
    // select in the full flow too; sport-only mode keeps its Golf fallback.
    sport: props.sportOnly ? props.user?.sport || "Golf" : props.user?.sport || null,
    // The title question is hidden in sport-only mode — keep the preset
    // instead of submitting null and clearing it.
    title: props.sportOnly ? props.user?.kindTitle || null : null,
  }));
  const [errorsForm, addErrors, clearErrors] = useError(initErrors);

  const kindChange = (ev: EventState) => {
    setState({ ...state, kind: ev.name as KindType });
  };

  const sportChange = (opt: DefaultOption) => {
    // The select echoes programmatic syncs (an empty one while options load,
    // then the controlled value itself) — react only to a real user change:
    // never wipe a prefilled sport, never loop on the echo.
    if (opt?.value && opt.value !== state.sport) {
      setState({ ...state, sport: opt.value });
    }
  };

  const titleChange = (opt: DefaultOption) => {
    if (opt != null) {
      setState({ ...state, title: opt.value });
    } else {
      setState({ ...state, title: null });
    }
  };

  const submitAbout = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearErrors();

    const kind = state.kind;
    const validation = validateProperty("kind", kind);

    if (!validation.isValid) {
      addErrors({ kind: validation.error });
      return;
    }

    let sport: string | null = (state.sport || "").trim();
    let title: string | null = (state.title || "").trim();

    if (!sport.length) {
      sport = null;
    }
    if (!title.length) {
      title = null;
    }

    (AuthService as any)
      .savePostSignUpSurvey(kind, sport, title)
      .then(() => {
        props.onComplete({ kind: kind.trim() as KindType, sport, title });
      });
  };

  const getError = (key: keyof FormErrors, errorsForm: FormErrors) => {
    if (errorsForm[key]) {
      return errorsForm[key];
    }
    return null;
  };

  return (
    <form className={styles.aboutContainer} noValidate onSubmit={submitAbout}>
      <h2 className={styles.about_you_title}>{t("About You")}</h2>
      <p className={styles.about_yourself}>
        {t(
          "Tell us a little bit about yourself so we can customize your experience.",
        )}
      </p>

      <div className={styles.aboutBody}>
        <div
          className={cls(
            styles.qBox,
            props.coachSignup || props.sportOnly ? "hidden" : "",
          )}
        >
          {/* <label>{t("What role best describes you?")}</label> */}
          <section className={styles.selector}>
            <span>
              <ImageCheckbox
                src={role_coach_png}
                name="coach"
                label={t("Coach")}
                checked={state.kind === "coach"}
                onChange={kindChange}
                controlled={true}
              />
            </span>
            <span>
              <ImageCheckbox
                src={role_athlete_png}
                name="athlete"
                label={t("Athlete")}
                checked={state.kind === "athlete"}
                onChange={kindChange}
                controlled={true}
              />
            </span>
            <span>
              <ImageCheckbox
                src={role_fan_png}
                name="other"
                label={t("Parent / Fan")}
                checked={state.kind === "other"}
                onChange={kindChange}
                controlled={true}
              />
            </span>
          </section>

          <FormItem error={getError("kind", errorsForm)}></FormItem>
        </div>

        <div className={styles.qBox}>
          <label>{t("What are you using CoachNow for?")}</label>
          <section>
            <SportSelect
              onChange={sportChange}
              isCreatable={true}
              value={state.sport || undefined}
            />
          </section>
        </div>

        <div className={cls(styles.qBox, props.sportOnly ? "hidden" : "")}>
          <label>{t("I like to be called...")}</label>
          <section>
            <RoleSelect type={state.kind} onChange={titleChange} />
          </section>
        </div>
      </div>

      <div className={styles.aboutFooter}>
        <button className="btn btn-primary full">{t("Get Started")}</button>
      </div>
    </form>
  );
}
